import React from 'react';
import FormSection from '../../../components/ui/FormSection';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PublishingSettings = ({ settings, onChange, errors }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('conferences');
  const k = (key) => t(`setupWizard.publishing.${key}`);

  const visibilityOptions = ['public', 'unlisted', 'private']?.map(value => ({
    value,
    label: k(`visibility.options.${value}`)
  }));

  const submissionStatusOptions = ['open', 'closed', 'upcoming']?.map(value => ({
    value,
    label: k(`submissionStatus.options.${value}`)
  }));

  const checklistItems = ['basicInfo', 'track', 'team', 'form', 'deadlines'];

  return (
    <div className="space-y-6 md:space-y-8">
      <FormSection title={k('visibilityTitle')} hasError={Boolean(errors?.visibility)}>
        <div className="space-y-4">
          <Select
            label={k('visibility.label')}
            options={visibilityOptions}
            value={settings?.visibility}
            onChange={(value) => onChange({ target: { name: 'visibility', value } })}
            error={errors?.visibility}
            required
            description={k('visibility.description')}
          />

          <Checkbox
            label={k('featured.label')}
            checked={settings?.featured}
            onChange={(e) => onChange({ target: { name: 'featured', value: e?.target?.checked } })}
            description={k('featured.description')}
          />

          <Checkbox
            label={k('publicRegistration.label')}
            checked={settings?.publicRegistration}
            onChange={(e) => onChange({ target: { name: 'publicRegistration', value: e?.target?.checked } })}
            description={k('publicRegistration.description')}
          />
        </div>
      </FormSection>
      <FormSection title={k('deadlinesTitle')} hasError={Boolean(errors?.paperDeadline || errors?.reviewDeadline || errors?.cameraReadyDeadline)}>
        <div className="space-y-4">
          <Select
            label={k('submissionStatus.label')}
            options={submissionStatusOptions}
            value={settings?.submissionStatus}
            onChange={(value) => onChange({ target: { name: 'submissionStatus', value } })}
            error={errors?.submissionStatus}
            required
          />

          <Input
            label={k('paperDeadline.label')}
            type="datetime-local"
            name="paperDeadline"
            value={settings?.paperDeadline}
            onChange={onChange}
            error={errors?.paperDeadline}
            required
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={k('reviewDeadline.label')}
              type="datetime-local"
              name="reviewDeadline"
              value={settings?.reviewDeadline}
              onChange={onChange}
              error={errors?.reviewDeadline}
              required
              description={k('reviewDeadline.description')}
            />

            <Input
              label={k('decisionDate.label')}
              type="datetime-local"
              name="decisionDate"
              value={settings?.decisionDate}
              onChange={onChange}
              error={errors?.decisionDate}
              required
              description={k('decisionDate.description')}
            />
          </div>

          <Input
            label={k('cameraReadyDeadline.label')}
            type="datetime-local"
            name="cameraReadyDeadline"
            value={settings?.cameraReadyDeadline}
            onChange={onChange}
            description={k('cameraReadyDeadline.description')}
          />
        </div>
      </FormSection>
      <FormSection title={k('notificationsTitle')} collapsible defaultOpen={false} optional>
        <div className="space-y-4">
          {['confirmationEmails', 'reminderEmails', 'reviewAssignmentEmails', 'decisionEmails']?.map((name) => (
            <Checkbox
              key={name}
              label={k(`${name}.label`)}
              checked={settings?.[name]}
              onChange={(e) => onChange({ target: { name, value: e?.target?.checked } })}
              description={k(`${name}.description`)}
            />
          ))}
        </div>
      </FormSection>
      <FormSection title={k('reviewSettingsTitle')} hasError={Boolean(errors?.minReviews)}>
        <div className="space-y-4">
          <Input
            label={k('minReviews.label')}
            type="number"
            name="minReviews"
            placeholder="3"
            value={settings?.minReviews}
            onChange={onChange}
            error={errors?.minReviews}
            required
            min="1"
            max="10"
            description={k('minReviews.description')}
          />

          {['enableBidding', 'doubleBlind', 'showOtherReviews']?.map((name) => (
            <Checkbox
              key={name}
              label={k(`${name}.label`)}
              checked={settings?.[name]}
              onChange={(e) => onChange({ target: { name, value: e?.target?.checked } })}
              description={k(`${name}.description`)}
            />
          ))}
        </div>
      </FormSection>
      <div className="bg-muted/50 rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{k('reviewSchema.title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {k('reviewSchema.description')}
            </p>
          </div>
          <Icon name="ClipboardList" size={32} className="text-primary flex-shrink-0" />
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              if (window.draftConferenceId) {
                window.open(`/review-schema-builder?conferenceId=${window.draftConferenceId}`, '_blank');
              } else {
                toastError(k('reviewSchema.saveDraftFirst'));
              }
            }}
            iconName="ExternalLink"
            iconPosition="left"
          >
            {k('reviewSchema.button')}
          </Button>
        </div>
      </div>
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 md:p-6">
        <div className="flex gap-3 md:gap-4">
          <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0" />
          <div>
            <h4 className="text-base font-semibold text-foreground mb-2">{k('checklist.title')}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {checklistItems?.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span>{k(`checklist.${item}`)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-foreground">
              {k('checklist.note')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishingSettings;
